import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom'
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import {
  CommonButton,
  CommonDialog,
  CommonSelect,
  CommonTextField,
  CommonCkHtmlEditor,
} from '@/components/common'
import {
  DRIVE_DONORS_EMAIL_MODES,
  DRIVE_DONORS_EMAIL_SOURCE,
  DRIVE_DONORS_RECIPIENT_MODES,
  buildDriveDonorsEmailReviewPath,
  buildDriveDonorsPath,
  getDriveDonorsDeliveryCounts,
  getDriveDonorsEmailSteps,
  getDriveDonorsSelectedCount,
  hasDriveDonorsEmailDraftContent,
  mapDriveDonorsTemplateOptions,
  stripDriveDonorsEmailHtml,
} from '@/components/chairperson/drive-donors/constants'
import { DriveDonorsRecipientsDialog } from '@/components/chairperson/drive-donors/DriveDonorsRecipientsDialog'
import { chairpersonService } from '@/services'
import { pickChairpersonDriveNavState } from '@/utils/chairpersonNavigation'

const EMPTY_DONOR_IDS = []
const EMPTY_GUEST_IDS = []

export const ChairpersonDriveDonorsComposeEmailPage = () => {
  const { driveId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedAccountId } = useOutletContext()

  const donorIds = useMemo(
    () => location.state?.donorIds ?? EMPTY_DONOR_IDS,
    [location.state?.donorIds]
  )
  const guestIds = useMemo(
    () => location.state?.guestIds ?? EMPTY_GUEST_IDS,
    [location.state?.guestIds]
  )
  const sourceTab = location.state?.sourceTab
  const recipientMode =
    location.state?.recipientMode ?? DRIVE_DONORS_RECIPIENT_MODES.SELECTED
  const accountId = location.state?.accountId ?? selectedAccountId
  const restoredSubject = location.state?.subject ?? ''
  const restoredEmailBody = location.state?.emailBody ?? ''
  const restoredTemplateId = location.state?.templateId
  const restoredTemplateName = location.state?.templateName
  const restoredEmailMode = location.state?.emailMode
  const restoredRecipientPreview = location.state?.recipientPreview
  const restoredComposeContext = location.state?.composeContext

  const hasAppliedDefaultTemplate = useRef(false)
  const donorsPath = buildDriveDonorsPath(driveId)
  const driveNavState = useMemo(
    () => pickChairpersonDriveNavState(location.state),
    [
      location.state?.returnTo,
      location.state?.fromTab,
      location.state?.driveName,
      location.state?.driveDate,
      location.state?.accountId,
    ]
  )

  const [loading, setLoading] = useState(true)
  const [composeContext, setComposeContext] = useState(restoredComposeContext)
  const [recipientPreview, setRecipientPreview] = useState(
    restoredRecipientPreview
  )
  const [selectedTemplate, setSelectedTemplate] = useState(
    restoredTemplateId
      ? {
          id: restoredTemplateId,
          name: restoredTemplateName ?? restoredTemplateId,
          emailMode: restoredEmailMode ?? DRIVE_DONORS_EMAIL_MODES.COMPOSED,
        }
      : null
  )
  const [subject, setSubject] = useState(restoredSubject)
  const [emailBody, setEmailBody] = useState(restoredEmailBody)
  const [errors, setErrors] = useState({})
  const [loadError, setLoadError] = useState('')
  const [showRecipientsDialog, setShowRecipientsDialog] = useState(false)
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)
  const [copyMessage, setCopyMessage] = useState('')

  const hasDraftContent = hasDriveDonorsEmailDraftContent(subject, emailBody)

  useEffect(() => {
    const requiresSelection =
      recipientMode === DRIVE_DONORS_RECIPIENT_MODES.SELECTED
    if (
      !sourceTab ||
      (requiresSelection && !donorIds.length && !guestIds.length)
    ) {
      navigate(donorsPath, {
        replace: true,
        state: { ...driveNavState, accountId },
      })
    }
  }, [
    donorIds.length,
    guestIds.length,
    sourceTab,
    recipientMode,
    navigate,
    donorsPath,
    accountId,
    driveNavState,
  ])

  useEffect(() => {
    const loadData = async () => {
      const requiresSelection =
        recipientMode === DRIVE_DONORS_RECIPIENT_MODES.SELECTED
      if (
        (requiresSelection && !donorIds.length && !guestIds.length) ||
        !accountId
      ) {
        return
      }

      setLoading(true)
      setLoadError('')
      try {
        const requests = [
          chairpersonService.getDriveDonorsEmailComposeContext(
            driveId,
            accountId
          ),
        ]
        if (!restoredRecipientPreview) {
          requests.push(
            chairpersonService.previewDriveDonorsEmailRecipients(
              driveId,
              accountId,
              {
                donor_ids: donorIds,
                guest_ids: guestIds,
                source_tab: sourceTab,
                source: DRIVE_DONORS_EMAIL_SOURCE,
                recipient_mode: recipientMode,
              }
            )
          )
        }

        const responses = await Promise.all(requests)
        let responseIndex = 0

        const contextResponse = responses[responseIndex]
        responseIndex += 1
        if (contextResponse?.status_code === 200) {
          setComposeContext(contextResponse.data)
        } else {
          setLoadError(
            contextResponse?.response ?? 'Failed to load email compose context.'
          )
        }

        if (!restoredRecipientPreview) {
          const previewResponse = responses[responseIndex]
          if (previewResponse?.status_code === 200) {
            setRecipientPreview(previewResponse.data)
          } else {
            setLoadError(
              previewResponse?.response ?? 'Failed to load recipient preview.'
            )
          }
        }
      } catch (error) {
        console.error('Failed to load email compose data', error)
        setLoadError(error?.message ?? 'Failed to load email compose data.')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [
    driveId,
    accountId,
    donorIds,
    guestIds,
    sourceTab,
    restoredRecipientPreview,
    recipientMode,
  ])

  const templateOptions = useMemo(
    () =>
      mapDriveDonorsTemplateOptions(
        composeContext?.ds_templates ?? [],
        composeContext
      ),
    [composeContext]
  )

  const isPreconfigured =
    selectedTemplate?.emailMode === DRIVE_DONORS_EMAIL_MODES.PRECONFIGURED
  const selectedEmailMode =
    selectedTemplate?.emailMode ?? DRIVE_DONORS_EMAIL_MODES.COMPOSED

  useEffect(() => {
    if (
      hasAppliedDefaultTemplate.current ||
      restoredTemplateId ||
      !composeContext?.default_ds_template_id ||
      !templateOptions.length
    ) {
      return
    }

    const match = templateOptions.find(
      option =>
        String(option.id) === String(composeContext.default_ds_template_id)
    )
    if (match) {
      setSelectedTemplate(match)
      hasAppliedDefaultTemplate.current = true
    }
  }, [
    composeContext?.default_ds_template_id,
    restoredTemplateId,
    templateOptions,
  ])

  useEffect(() => {
    if (!restoredTemplateId || !templateOptions.length) {
      return
    }

    const match = templateOptions.find(
      option => String(option.id) === String(restoredTemplateId)
    )
    if (match) {
      setSelectedTemplate(match)
    }
  }, [restoredTemplateId, templateOptions])

  const mergeFields = composeContext?.merge_fields ?? []
  const accountName =
    composeContext?.drive?.account_name ??
    recipientPreview?.drive?.account_name ??
    ''
  const selectedCount =
    recipientMode === DRIVE_DONORS_RECIPIENT_MODES.SELECTED
      ? getDriveDonorsSelectedCount({ donorIds, guestIds })
      : Number(recipientPreview?.total_selected) || 0
  const recipientModeLabel =
    recipientMode === DRIVE_DONORS_RECIPIENT_MODES.ALL_DONORS
      ? 'all donors'
      : recipientMode === DRIVE_DONORS_RECIPIENT_MODES.ALL_ELIGIBLE_DONORS
        ? 'all eligible donors'
        : 'selected donors'
  const { willReceive, willSkip } =
    getDriveDonorsDeliveryCounts(recipientPreview)
  const recipientPreviewLoading = loading && !recipientPreview

  const handleCopyVariable = async variable => {
    try {
      await navigator.clipboard.writeText(variable)
      setCopyMessage('Merge field copied.')
    } catch {
      setCopyMessage('Unable to copy merge field.')
    }
  }

  const validateForm = () => {
    const nextErrors = {}
    if (!selectedTemplate?.id) {
      nextErrors.template = 'Flow email template is required.'
    }
    if (!isPreconfigured) {
      if (!subject?.trim()) {
        nextErrors.subject = 'Subject is required.'
      }
      if (!stripDriveDonorsEmailHtml(emailBody)) {
        nextErrors.emailBody = 'Email body is required.'
      }
    }
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleNext = () => {
    if (!validateForm()) return

    navigate(buildDriveDonorsEmailReviewPath(driveId), {
      state: {
        ...driveNavState,
        donorIds,
        guestIds,
        sourceTab,
        recipientMode,
        accountId,
        templateId: selectedTemplate.id,
        templateName: selectedTemplate.name,
        emailMode: selectedTemplate.emailMode,
        subject,
        emailBody,
        recipientPreview,
        composeContext,
      },
    })
  }

  const leaveDonorsList = () => {
    navigate(donorsPath, { state: { ...driveNavState, accountId } })
  }

  const requestLeaveDonorsList = () => {
    if (hasDraftContent) {
      setShowDiscardDialog(true)
      return
    }
    leaveDonorsList()
  }

  const handleConfirmDiscard = () => {
    setShowDiscardDialog(false)
    leaveDonorsList()
  }

  const handleCancelDiscard = () => {
    setShowDiscardDialog(false)
  }

  return (
    <Box>
      <CommonButton
        variant='text'
        startIcon={<ArrowBackIosNewOutlinedIcon sx={{ fontSize: 14 }} />}
        onClick={requestLeaveDonorsList}
        sx={{ mb: 2 }}
      >
        Back to Donors
      </CommonButton>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent='space-between'
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction='row' spacing={2} alignItems='center'>
          <EmailOutlinedIcon color='primary' sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant='h5' fontWeight={700}>
              {isPreconfigured ? 'Select Email Template' : 'Email to Donors'}
            </Typography>
            {accountName ? (
              <Typography variant='body2' color='text.secondary'>
                {accountName}
              </Typography>
            ) : null}
          </Box>
        </Stack>
        <Stepper activeStep={1} sx={{ minWidth: 280 }}>
          {getDriveDonorsEmailSteps(selectedEmailMode).map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Stack>

      {loadError ? (
        <Alert severity='error' sx={{ mb: 3 }}>
          {loadError}
        </Alert>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          bgcolor: 'grey.100',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
        }}
      >
        <Stack direction='row' spacing={2} alignItems='flex-start'>
          <GroupOutlinedIcon color='action' />
          <Box>
            <Typography variant='subtitle1' fontWeight={600}>
              {selectedCount} {recipientModeLabel} from{' '}
              {accountName || 'this account'}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {recipientPreviewLoading
                ? 'Loading recipient details...'
                : `${willReceive} will receive this email. ${willSkip} will be skipped.`}
            </Typography>
          </Box>
        </Stack>
        <CommonButton
          variant='secondary'
          onClick={() => setShowRecipientsDialog(true)}
          disabled={recipientPreviewLoading}
        >
          View Recipients
        </CommonButton>
      </Paper>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
        <Paper
          elevation={0}
          sx={{ p: 2, flex: 2, bgcolor: 'background.paper' }}
        >
          <Stack spacing={2}>
            <CommonSelect
              id='flow-email-template'
              label='Flow Email Template*'
              value={selectedTemplate}
              options={templateOptions}
              loading={loading && !composeContext}
              error={errors.template}
              helperText={errors.template}
              placeholder='Select a template'
              returnObject
              onChange={setSelectedTemplate}
              disabled={!templateOptions.length}
            />

            {!loading &&
            composeContext &&
            !composeContext.campaign_configured ? (
              <Alert severity='warning'>
                Chairperson Drive Donors Email campaign is not configured for
                this tenant.
              </Alert>
            ) : null}

            {!loading &&
            composeContext?.campaign_configured &&
            !templateOptions.length ? (
              <Alert severity='warning'>
                No Flow email templates found for the configured Drive Donors
                Email campaign.
              </Alert>
            ) : null}

            {!isPreconfigured ? (
              <>
                <CommonTextField
                  id='email-subject'
                  label='Subject*'
                  value={subject}
                  setValue={setSubject}
                  error={errors.subject}
                  helperText={errors.subject}
                  fullWidth
                />

                <CommonCkHtmlEditor
                  id='email-body'
                  label='Email Body*'
                  value={emailBody}
                  onChange={html => {
                    setEmailBody(html)
                    setErrors(prev => ({ ...prev, emailBody: '' }))
                  }}
                  error={errors.emailBody}
                  helperText={
                    errors.emailBody
                      ? undefined
                      : 'Merge fields from the panel can be pasted into the body.'
                  }
                  required
                  editorKey={`drive-donors-compose-${selectedTemplate?.id ?? 'new'}`}
                  disabled={loading && !composeContext}
                />
              </>
            ) : null}
          </Stack>
        </Paper>

        {!isPreconfigured ? (
          <Paper elevation={0} sx={{ p: 2, flex: 1, bgcolor: 'grey.50' }}>
            <Typography variant='subtitle1' fontWeight={700} sx={{ mb: 1 }}>
              Merge Fields
            </Typography>
            {copyMessage ? (
              <Typography
                variant='caption'
                color='success.main'
                sx={{ mb: 1, display: 'block' }}
              >
                {copyMessage}
              </Typography>
            ) : null}
            {loading && !composeContext ? (
              <Typography variant='body2' color='text.secondary'>
                Loading merge fields...
              </Typography>
            ) : (
              <Stack spacing={1}>
                {mergeFields.map(field => (
                  <Stack
                    key={field.variable}
                    direction='row'
                    spacing={1}
                    alignItems='center'
                    justifyContent='space-between'
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant='body2' fontWeight={600}>
                        {field.label}
                      </Typography>
                      <Chip
                        size='small'
                        label={field.variable}
                        sx={{ maxWidth: '100%' }}
                      />
                    </Box>
                    <CommonButton
                      variant='text'
                      size='small'
                      iconOnly={<ContentCopyOutlinedIcon />}
                      onClick={() => handleCopyVariable(field.variable)}
                      aria-label={`Copy ${field.variable}`}
                    />
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
        ) : null}
      </Stack>

      <Stack
        direction='row'
        justifyContent='flex-end'
        spacing={2}
        sx={{ mt: 3 }}
      >
        <CommonButton variant='secondary' onClick={requestLeaveDonorsList}>
          Cancel
        </CommonButton>
        <CommonButton variant='primary' onClick={handleNext}>
          Next
        </CommonButton>
      </Stack>

      <DriveDonorsRecipientsDialog
        open={showRecipientsDialog}
        onClose={() => setShowRecipientsDialog(false)}
        recipients={recipientPreview?.recipients ?? []}
        skipped={recipientPreview?.skipped ?? []}
      />

      <CommonDialog
        open={showDiscardDialog}
        onClose={handleCancelDiscard}
        title='Discard changes?'
        actions={
          <>
            <CommonButton variant='secondary' onClick={handleCancelDiscard}>
              Keep editing
            </CommonButton>
            <CommonButton variant='primary' onClick={handleConfirmDiscard}>
              Discard
            </CommonButton>
          </>
        }
      >
        <Typography variant='body1' sx={{ px: 2, pb: 2 }}>
          Unsaved changes will be lost. Do you want to continue?
        </Typography>
      </CommonDialog>
    </Box>
  )
}
